import clsx from "clsx"
import * as React from "react"

import clsxm from "@/lib/clsxm"

import { SummaryBlock } from "@/components/Booking/BookingSummary"
import Heading from "@/components/common/Heading/Heading"
import Icon from "@/components/common/Icon/Icon"
import NextImage from "@/components/common/NextImage/NextImage"

import { LightningIcon, MedalIcon, UnlockIcon } from "@/constants/icons"

import offeringImage from "../../../public/images/homepage/appointment-example-bg.png"
import OfferingDivider from "../../../public/svg/homepage/offering-divider.svg"

const Offering = () => {
  return (
    <section
      className={clsxm(
        `relative mt-0 flex flex-col items-center justify-center`,
        `sm: sm:mb-20`
      )}
    >
      <OfferingDivider className={`w-full`} />
      <div
        className={clsx(
          `relative z-10 w-full max-w-7xl px-4 py-8`,
          `sm:grid sm:grid-cols-12 sm:justify-between`
        )}
      >
        <div
          className={clsx(
            `relative col-span-5 col-end-5 hidden`,
            `sm:block lg:col-span-7 lg:col-end-7`
          )}
        >
          <div>
            <div
              className={`absolute right-0 top-1/2 h-auto w-full min-w-[500px] -translate-y-1/2`}
            >
              <NextImage
                priority
                src={offeringImage}
                alt={``}
                className={`w-full`}
              />
            </div>
            <div
              className={clsx(
                `absolute right-0 top-1/2 w-[330px] origin-right -translate-y-1/2 scale-[.65] space-y-3`,
                `md:scale-75`,
                `lg:scale-100`
              )}
            >
              <SummaryBlock.Timeslot
                style={`white`}
                timeslot={new Date(`August 19, 2022 11:00:00`)}
              />
              <SummaryBlock.Location
                address_1={`101 Colombo Street`}
                address_2={`Beckenham`}
                post_code={`8023`}
                practice={`Christchurch Osteopathic Centre`}
                style={`white`}
              />
              <SummaryBlock.AppointmentType
                style={`white`}
                appointmentType={`First appointment`}
                practitioner={`Dr Colin Lim`}
              />
            </div>
          </div>
        </div>
        <div
          className={clsx(
            `sm:col-span-6 sm:col-start-6`,
            `lg:col-span-4 lg:col-start-8`
          )}
        >
          <Heading
            className={`mb-10 font-baskervilleHeadings text-4xl tracking-tight`}
            headingLevel={`h2`}
          >
            Why wellness <span className={`inline-block`}>seekers love us</span>
          </Heading>
          <div className={`space-y-10`}>
            <OfferingItem
              icon={LightningIcon}
              heading={`Simple, quick and easy`}
              bodyParagraph={`Life can be stressful, booking appointments shouldn't be. WellNow makes it easy to find and book available appointments.`}
            />
            <OfferingItem
              icon={MedalIcon}
              heading={`Immediate availability`}
              bodyParagraph={`WellNow offers appointments for the next 7 days - so you can book when you need it most.`}
            />
            <OfferingItem
              icon={UnlockIcon}
              heading={`No extra fees`}
              bodyParagraph={`The price on WellNow is the same as booking direct with a practitioner.  Giving you confidence to book instantly.`}
            />
          </div>
        </div>
      </div>
    </section>
  )
}

type OfferingItemProps = {
  heading: string
  bodyParagraph: string
  icon: React.ElementType
  iconClassName?: string
  iconSize?:
    | `auto`
    | `xs-16`
    | `sm-20`
    | `md-24`
    | `lg-32`
    | `xl-40`
    | `2xl-48`
    | `3xl-56`
} & React.ComponentPropsWithoutRef<`div`>

export function OfferingItem({
  className,
  heading,
  bodyParagraph,
  icon,
  iconSize,
  iconClassName,
  ...rest
}: OfferingItemProps) {
  return (
    <div
      className={clsxm(`flex items-start justify-start`, className)}
      {...rest}
    >
      <div className={`mr-6`}>
        <div
          className={`flex h-12 w-12 items-center justify-center rounded border border-primary-300 text-primary-500`}
        >
          <Icon
            className={iconClassName}
            size={iconSize || `auto`}
            icon={icon}
          />
        </div>
      </div>
      <div>
        <p className={`mb-2 text-lg font-bold leading-tight`}>{heading}</p>
        <p className={`text-sm`}>{bodyParagraph}</p>
      </div>
    </div>
  )
}

export default Offering
